import SolarMaximizeSquareOutline from '@/components-v2/icons/SolarMaximizeSquareOutline';
import SolarMinimizeSquareOutline from '@/components-v2/icons/SolarMinimizeSquareOutline';
import { CCTextarea } from '@/components/core/cc-textarea';
import { useToast } from '@/components/toast';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { useTranslate } from '@tolgee/react';
import { Fragment, forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

// ======= 常量和类型定义 =======
// 段落颜色配置
const SEGMENT_COLORS = {
  '--segment-color-1': '#fde68a',
  '--segment-color-2': '#bfdbfe',
  '--segment-color-3': '#bbf7d0',
  '--segment-color-4': '#fed7aa',
  '--segment-color-5': '#ddd6fe',
  '--segment-color-6': '#fecaca',
  '--segment-color-7': '#a5f3fc',
  '--segment-color-8': '#e9d5ff',
} as const;

// 文本处理相关常量
const SEGMENT_SEPARATOR_REGEX = /([,，])/g;
const EMOJI_REGEX =
  /[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2600-\u27FF]|[\uD83C][\uDF00-\uDFFF]|[\uD83D][\uDC00-\uDE4F]|[\uD83D][\uDE80-\uDEFF]/g;
const MIN_ROWS = 2;
const MAX_ROWS_COLLAPSED = 6;
const MAX_ROWS_EXPANDED = 100;
const MAX_LENGTH = 2000;

// 组件属性类型
interface PromptTextareaProps {
  /** 组件名称，用于表单识别 */
  name?: string;
  /** 文本内容值 */
  value?: string;
  /** 占位符文本 */
  placeholder?: string;
  /** 值变化回调函数 */
  onChange?: (value: string) => void;
  /** 组件模式：read-只读模式，write-编辑模式 */
  mode?: 'read' | 'write';
  /** 焦点获得回调 */
  onFocus?: () => void;
  /** 焦点失去回调 */
  onBlur?: () => void;
  /** 错误状态 */
  error?: boolean;
  /** 错误提示文本 */
  helperText?: string;
}

// 段落处理结果类型
interface SegmentResult {
  leadingWhitespace: string;
  content: string;
  trailingWhitespace: string;
}

// ======= 工具函数 =======
// 处理分段文本，分离两端空白字符
function processTrimmedSegment(text: string): SegmentResult {
  const leadingMatch = text.match(/^(\s+)/);
  const leadingWhitespace = leadingMatch ? leadingMatch[0] : '';

  const trailingMatch = text.match(/(\s+)$/);
  const trailingWhitespace = trailingMatch ? trailingMatch[0] : '';

  const content = text.slice(leadingWhitespace.length, text.length - trailingWhitespace.length);

  return { leadingWhitespace, content, trailingWhitespace };
}

// ======= 样式组件 =======
// 主容器样式
const StyledBox = styled(Box)(({ theme }) => ({
  ...SEGMENT_COLORS,
  position: 'relative',
  width: '100%',
  borderRadius: '12px',
  outline: 'none',
  color: grey[900],
  backgroundColor: theme.palette.background.neutral,
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.2s ease-in-out',
  height: 'auto',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  fontFamily: "'IBM Plex Sans', sans-serif",

  '&:focus-visible': { outline: 0 },

  '@media (hover: hover)': {
    '&:hover:not(:focus-within)': {
      borderColor: theme.palette.grey[400],
      boxShadow: '0 2px 4px rgba(145, 158, 171, 0.08)',
    },
  },

  '&:empty:before': {
    content: 'attr(data-placeholder)',
    color: '#757575',
  },

  '& .segment': {
    display: 'inline',
    borderRadius: '6px',
    transition: 'background-color 0.2s ease',
    userSelect: 'none',
    padding: '0.05em 0.2em',
    margin: '-0.05em -0.2em',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    verticalAlign: 'baseline',
    lineHeight: 'inherit',
    position: 'relative',
  },

  // 高亮状态处理 - 优化只读模式支持
  ...Array.from({ length: 8 }, (_, i) => i + 1).reduce(
    (acc, index) => {
      // 焦点内激活段落高亮
      acc[`&:focus-within .segment.color-${index}.active`] = {
        backgroundColor: `var(--segment-color-${index})`,
      };
      // 悬浮时全部段落高亮（包括只读模式）
      acc[`@media (hover: hover)`] = acc[`@media (hover: hover)`] || {};
      acc[`@media (hover: hover)`][`&:hover:not(:focus-within) .segment.color-${index}`] = {
        backgroundColor: `var(--segment-color-${index})`,
      };
      return acc;
    },
    {} as Record<string, any>,
  ),
}));

// 显示层样式
const DisplayBox = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'transparent',
  zIndex: 0,
  padding: '6px 10px',
  paddingBottom: 'calc(8px + 1.5em)',
  overflow: 'hidden',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: "'IBM Plex Sans', sans-serif",
  '& > *:not(.segment)': {
    pointerEvents: 'none',
  },
  // 隐藏所有文本内容
  color: 'transparent',
});

// 展开按钮容器样式
const ExpandButtonContainer = styled(Box)({
  position: 'absolute',
  bottom: '8px',
  right: '8px',
  zIndex: 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transform: 'translateY(4px) scale(0.95)',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0) scale(1)',
  },
});

// 展开图标包装器样式
const ExpandIconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '.expanded &': {
    transform: 'rotate(180deg)',
  },
});

/**
 * PromptTextarea - 高级文本输入组件
 */
export const PromptTextarea = forwardRef<HTMLDivElement, PromptTextareaProps>(
  ({ name, value = '', placeholder, onChange, mode = 'write', onFocus, onBlur, error, helperText }, ref) => {
    const id = useId();
    const [innerValue, setInnerValue] = useState<string>(value);
    const [expanded, setExpanded] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const displayRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isUserInputRef = useRef(false);
    const { showToast } = useToast();
    const { t } = useTranslate();

    // 将外部ref与内部containerRef连接
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(containerRef.current);
        } else {
          ref.current = containerRef.current;
        }
      }
    }, [ref]);

    useEffect(() => {
      setInnerValue(value);
      isUserInputRef.current = false; // 外部值变化，非用户输入
    }, [value]);

    // 获取光标位置所在的段落索引（仅计算有实际内容的段落）
    const getActiveSegmentIndex = useCallback((text: string, cursorPosition: number) => {
      const segments = text.split(SEGMENT_SEPARATOR_REGEX);
      let position = 0;
      let validSegmentIndex = -1;
      let lastValidSegmentIndex = -1;

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (!segment) continue;

        if (i % 2 === 0) {
          // 只有包含非空白内容的段落才计入索引
          const { content } = processTrimmedSegment(segment);
          if (content.trim()) {
            validSegmentIndex++;
            lastValidSegmentIndex = validSegmentIndex;

            if (cursorPosition >= position && cursorPosition <= position + segment.length) {
              return validSegmentIndex;
            }
          }
        } else if (cursorPosition === position + segment.length && i + 1 < segments.length) {
          return validSegmentIndex + 1;
        }

        position += segment.length;
      }

      return lastValidSegmentIndex;
    }, []);

    // 更新显示视图
    const updateDisplay = useCallback(
      (text: string, cursorPosition: number) => {
        const display = displayRef.current;
        if (!display) return;

        const fragment = document.createDocumentFragment();
        const segments = text.split(SEGMENT_SEPARATOR_REGEX);
        const currentSegmentIndex = cursorPosition >= 0 ? getActiveSegmentIndex(text, cursorPosition) : -1;

        let validSegmentCounter = 0;

        segments.forEach((segment) => {
          if (!segment) return;

          if (segment === ',' || segment === '，') {
            const commaSpan = document.createElement('span');
            commaSpan.className = 'comma';
            commaSpan.textContent = segment;
            fragment.appendChild(commaSpan);
          } else {
            const { leadingWhitespace, content, trailingWhitespace } = processTrimmedSegment(segment);

            // 添加前导空白字符
            if (leadingWhitespace) {
              fragment.appendChild(document.createTextNode(leadingWhitespace));
            }

            // 只为非空内容创建高亮段落
            if (content.trim()) {
              const span = document.createElement('span');
              const colorIndex = (validSegmentCounter % 8) + 1;
              span.className = `segment color-${colorIndex}${validSegmentCounter === currentSegmentIndex ? ' active' : ''}`;
              span.setAttribute('role', 'mark');
              span.textContent = content;
              fragment.appendChild(span);
              validSegmentCounter++;
            } else if (content) {
              // 纯空白内容直接添加为文本节点，不高亮
              fragment.appendChild(document.createTextNode(content));
            }

            // 添加尾随空白字符
            if (trailingWhitespace) {
              fragment.appendChild(document.createTextNode(trailingWhitespace));
            }
          }
        });

        display.replaceChildren(fragment);
      },
      [getActiveSegmentIndex],
    );

    // 智能高亮同步：区分外部值变化和用户输入
    useLayoutEffect(() => {
      if (displayRef.current) {
        if (isUserInputRef.current) {
          // 用户输入时，handleChange已经更新了高亮，跳过以避免闪烁
          isUserInputRef.current = false;
        } else {
          // 外部值变化（如apply参数）时，强制更新高亮
          updateDisplay(innerValue, -1);
        }
      }
    }, [innerValue, updateDisplay]);

    // 检查内容是否溢出
    const checkOverflow = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
      const paddingTop = parseFloat(getComputedStyle(textarea).paddingTop);
      const paddingBottom = parseFloat(getComputedStyle(textarea).paddingBottom);
      const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom;
      const lines = Math.ceil(contentHeight / lineHeight);

      setHasOverflow(lines > MAX_ROWS_COLLAPSED);
    }, []);

    // 同步滚动位置
    const syncScroll = useCallback(() => {
      const textarea = textareaRef.current;
      const display = displayRef.current;
      if (textarea && display) {
        display.scrollTop = textarea.scrollTop;
      }
    }, []);

    // 处理文本变化
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        let finalValue = newValue;

        // 全局 Emoji 过滤
        const emojiFiltered = newValue.replace(EMOJI_REGEX, '');
        if (emojiFiltered !== newValue) {
          showToast('generate.emoji_not_allowed', 'error');
          finalValue = emojiFiltered;
        }

        // 长度限制
        if (finalValue.length > MAX_LENGTH) {
          finalValue = finalValue.slice(0, MAX_LENGTH);
          showToast('generate.content_trimmed', 'error');
        }

        const cursorPosition = e.target.selectionStart;
        isUserInputRef.current = true; // 标记为用户输入
        setInnerValue(finalValue);
        onChange?.(finalValue);
        updateDisplay(finalValue, cursorPosition);
        syncScroll();
        checkOverflow();
      },
      [onChange, updateDisplay, syncScroll, checkOverflow, showToast, t],
    );

    // 处理三次点击事件
    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLTextAreaElement>) => {
        if (e.detail !== 3) return;

        e.preventDefault();
        const textarea = e.currentTarget;
        const text = textarea.value;
        const clickPosition = textarea.selectionStart;
        const segments = text.split(SEGMENT_SEPARATOR_REGEX);
        let position = 0;

        for (let i = 0; i < segments.length; i += 2) {
          const segmentStart = position;
          const segmentLength = segments[i]?.length || 0;

          if (clickPosition >= segmentStart && clickPosition <= segmentStart + segmentLength) {
            textarea.setSelectionRange(segmentStart, segmentStart + segmentLength);
            updateDisplay(text, segmentStart);
            break;
          }

          position += segmentLength + (segments[i + 1]?.length || 0);
        }
      },
      [updateDisplay],
    );

    // 处理选择事件
    const handleSelect = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const text = textarea.value;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      if (start === 0 && end === text.length) {
        updateDisplay(text, -1);
        return;
      }

      updateDisplay(text, start);
    }, [updateDisplay]);

    // 添加滚动事件监听
    useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.addEventListener('scroll', syncScroll);
      return () => {
        textarea.removeEventListener('scroll', syncScroll);
      };
    }, [syncScroll]);

    // 切换展开/收起状态
    const toggleExpand = useCallback(() => {
      setExpanded((prev) => !prev);
      setTimeout(checkOverflow, 50);
    }, [checkOverflow]);

    // 处理焦点事件
    const handleFocus = useCallback(() => {
      onFocus?.();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
      onBlur?.();
    }, [onBlur]);

    return (
      <Fragment>
        <StyledBox ref={containerRef}>
          <CCTextarea
            ref={textareaRef}
            id={`prompt-textarea-${id}`}
            value={innerValue}
            onChange={handleChange}
            onSelect={handleSelect}
            onMouseDown={handleMouseDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            data-name={name}
            minRows={MIN_ROWS}
            maxRows={expanded ? MAX_ROWS_EXPANDED : MAX_ROWS_COLLAPSED}
            disabled={mode === 'read'}
            readOnly={mode === 'read'}
            aria-multiline="true"
            aria-label={placeholder}
          />
          <DisplayBox ref={displayRef} aria-hidden="true" role="presentation" />

          {/* 展开/收起按钮 */}
          {(hasOverflow || expanded) && (
            <ExpandButtonContainer className="visible">
              <Tooltip title={expanded ? t('common.collapse') : t('common.expand')} placement="top">
                <IconButton
                  onClick={toggleExpand}
                  size="small"
                  className={expanded ? 'expanded' : ''}
                  aria-expanded={expanded}
                  aria-label={expanded ? t('common.collapse') : t('common.expand')}
                  sx={{
                    backgroundColor: (theme) =>
                      expanded ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.background.paper, 0.9),
                    boxShadow: (theme) =>
                      expanded
                        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                        : `0 1px 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@media (hover: hover)': {
                      '&:hover': {
                        backgroundColor: (theme) =>
                          expanded ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.grey[200], 0.95),
                        transform: 'scale(1.1)',
                        boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                    width: 22,
                    height: 22,
                    padding: 0,
                  }}
                >
                  <ExpandIconWrapper className={expanded ? 'expanded' : ''}>
                    <Box
                      component={expanded ? SolarMinimizeSquareOutline : SolarMaximizeSquareOutline}
                      width={14}
                      sx={{
                        color: (theme) => (expanded ? theme.palette.primary.main : theme.palette.text.primary),
                      }}
                    />
                  </ExpandIconWrapper>
                </IconButton>
              </Tooltip>
            </ExpandButtonContainer>
          )}
        </StyledBox>
        {error && (
          <Typography variant="caption" color="error">
            {helperText}
          </Typography>
        )}
      </Fragment>
    );
  },
);

PromptTextarea.displayName = 'PromptTextarea';
